/**
 * Hand-off picker for a message or task.
 *
 * Lists the driver's manager (pre-selected), team lead, same-team peers, and the
 * standing coverage groups. Each row shows the person's relation to the driver.
 * Submitting hands the item off (and, for messages, marks it read upstream).
 */

import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import { useState } from "react";

import type { AssignmentOption } from "../assignments";
import { accent, text, zinc } from "../colors";
import { ActionButton } from "./primitives";

export function TaskAssignmentDialog({
  title,
  subtitle,
  options,
  onSubmit,
  onCancel,
}: {
  title: string;
  subtitle?: string;
  options: AssignmentOption[];
  onSubmit: (option: AssignmentOption) => void;
  onCancel: () => void;
}) {
  const [selectedId, setSelectedId] = useState(options[0]?.id ?? "");
  const selected = options.find((o) => o.id === selectedId) ?? options[0];

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: { bgcolor: zinc[900], border: `1px solid ${zinc[700]}`, borderRadius: "14px", backgroundImage: "none" },
        },
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: text.primary }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: text.muted, mb: 1.75 }}>
          {subtitle ?? "Hand this off to another driver manager or a coverage group."}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 360, overflowY: "auto" }}>
          {options.map((option) => {
            const active = option.id === selectedId;
            return (
              <Box
                key={option.id}
                component="button"
                type="button"
                onClick={() => setSelectedId(option.id)}
                sx={{
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 1.25,
                  py: 1,
                  borderRadius: "10px",
                  bgcolor: active ? accent.blueSoft : zinc[950],
                  border: `1px solid ${active ? accent.blueBorder : zinc[800]}`,
                  transition: "all 120ms ease",
                  "&:hover": { borderColor: active ? accent.blueBorder : zinc[700] },
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    flexShrink: 0,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: option.kind === "group" ? accent.purple : accent.blue,
                    bgcolor: option.kind === "group" ? accent.purpleSoft : accent.blueSoft,
                  }}
                >
                  {option.kind === "group" ? (
                    <GroupsIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <PersonIcon sx={{ fontSize: 18 }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: text.primary }}>{option.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: text.muted }}>{option.role}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <ActionButton label="Cancel" variant="ghost" onClick={onCancel} />
          <ActionButton label="Submit" onClick={() => selected && onSubmit(selected)} />
        </Box>
      </Box>
    </Dialog>
  );
}
